import { useApi, useToaster } from "@shopexperts/shared";
import { useQuery } from "@tanstack/vue-query";
import { computed, type Ref, toValue, watch } from "vue";

export type ExpertPlanStatusFilter = "all" | "active" | "inactive";

interface UseAdminExpertPlansOptions {
  status?: Ref<ExpertPlanStatusFilter> | ExpertPlanStatusFilter;
}

/**
 * Loads the admin Expert Plans list. Shares the root query key
 * ["admin", "expert-plans"] with `useExpertSubscription`, so creating /
 * updating / deleting plans automatically refreshes the plan picker shown
 * inside an expert's billing aside.
 */
export function useAdminExpertPlans(options: UseAdminExpertPlansOptions = {}) {
  const api = useApi();
  const toaster = useToaster();

  const statusValue = computed<ExpertPlanStatusFilter>(() => {
    const next = toValue(options.status ?? "all");
    return next ?? "all";
  });

  const query = useQuery({
    queryKey: computed(() => ["admin", "expert-plans", { status: statusValue.value }]),
    queryFn: async () => {
      const status = statusValue.value;
      const queryParams: { is_active?: boolean } = {};
      if (status === "active") queryParams.is_active = true;
      if (status === "inactive") queryParams.is_active = false;

      return await api.admin.expertSubscriptions.listPlans({ query: queryParams });
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
  });

  watch(query.error, (err) => {
    if (!err) return;
    toaster.create({
      title: "Failed to load expert plans",
      description: err.message,
      type: "error",
    });
    console.error("[useAdminExpertPlans] Error:", err);
  });

  const plans = computed(() => query.data.value?.plans ?? []);
  const defaultTermDays = computed(() => query.data.value?.default_term_days ?? null);

  return {
    plans,
    defaultTermDays,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
}

export default useAdminExpertPlans;
